import { LitElement, html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { classMap } from 'lit-html/directives/class-map.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';
import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { isDealerPage, logErrorToSentry } from '@Helpers/helpers';
import {
  analyticsCtaDataObject,
  handleAdobeNavEvent,
} from '../../helpers/adobe-analytics';
import { pushDataLayerEvent } from '../../helpers/data-layer';

import { styles } from './css';

class GlobalFooter extends LitElement {
  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
    ];
  }

  static properies = {
    addTopMargin: { type: Boolean },
  };

  constructor() {
    super();
    this.addTopMargin = !document.getElementsByTagName('ralph-block').length;
  }

  async connectedCallback() {
    super.connectedCallback();

    if (isDealerPage()) {
      this.sendAnalyticsDealerPageEvent();
    }

    await this.talkdeskChatInit(
      window,
      document,
      'tdWebchat',
      'https://talkdeskchatsdk.talkdeskapp.com/v2/talkdeskchatsdk.js',
      {
        touchpointId: 'e163ab949c994a61b0873643e8ca9861',
        accountId: '',
        region: 'td-us-1',
      },
      {
        enableValidation: false,
        enableEmoji: true,
        enableUserInput: true,
        enableAttachments: true,
      }
    );
  }

  render() {
    return html`
      <footer
        style="${styleMap(this.backgroundColor)}"
        class="block-grid footer-layout"
        class="${classMap({
          'block-grid': true,
          'footer-layout': true,
          'add-top-margin': this.addTopMargin,
        })}"
      >
        <div class="main">
          <div class="header">
            <h1>${unsafeHTML(this.withLineBreaks(this.mainData.header))}</h1>
            <cta-text
              cta-data=${this.primaryCtaData}
              .ctaAnalyticsData=${analyticsCtaDataObject(
                this.primaryCtaData,
                `Global Footer`
              )}
            ></cta-text>
          </div>
          <ul class="socials">
            ${this.mainData.socials.map(social => {
              return html`
                <li>
                  <a
                    @click="${event => {
                      handleAdobeNavEvent({
                        navString: social.navString,
                        destinationUrl: social.href,
                        target_blank: true,
                        optional_rel: 'noopener',
                        event,
                      });
                    }}"
                    tabindex="1"
                    href="${social.href}"
                  >
                    <img src=${social.svgSrc} class="footer-icon" />
                  </a>
                </li>
              `;
            })}
          </ul>
        </div>
        <div class="drivers">
          ${this.driversData.map(driverSection => {
            return html`
              <div class="driver-section">
                <p class="section-header">${driverSection.category}</p>
                ${driverSection.links.map(link => {
                  if (isDealerPage()) {
                    if (link.division && link.division === '3form')
                      return html``;
                  } else {
                    if (link.division && link.division === 'elements')
                      return html``;
                  }
                  return html`
                    <a
                      href=${ifDefined(link.href ? link.href : undefined)}
                      tabindex="1"
                      data-name=${link.text}
                      @click=${event => {
                        // INP links never navigate in handleAdobeNavEvent, so
                        // the browser has to follow their tel: and mailto:.
                        if (!link.navString.startsWith('INP')) {
                          event.preventDefault();
                        }
                        handleAdobeNavEvent({
                          navString: link.navString,
                          destinationUrl: link.href,
                          event,
                          target: `${link.target ? link.target : '_self'}`,
                        });
                        if (link.opensChat) this.openChat();
                      }}
                    >
                      ${link.svgSrc
                        ? html`
                            <img
                              src=${link.svgSrc}
                              class="footer-icon driver-icon"
                            />
                          `
                        : ``}
                      ${link.text}
                    </a>
                  `;
                })}
              </div>
            `;
          })}
        </div>
        <div class="terms">
          <ul class="left">
            <a><li>&copy; ${new Date().getFullYear()} 3form, LLC.</li></a>
          </ul>
          <ul class="right">
            ${this.termsData.right.map(term => {
              return html`
                <li>
                  <a
                    @click=${event => {
                      event.preventDefault();
                      handleAdobeNavEvent({
                        navString: term.navString,
                        destinationUrl: term.link,
                        event,
                        target: `${term.target ? term.target : '_self'}`,
                      });
                    }}
                    href="${term.link}"
                  >
                    ${term.text}
                  </a>
                </li>
              `;
            })}
          </ul>
        </div>
      </footer>
    `;
  }

  // Presses Talkdesk's own launcher, which does nothing while the chat is open.
  openChat() {
    document.getElementById('talkdesk-chat-widget-trigger')?.click();
  }

  talkdeskChatInit(window, document, nodeId, scriptSrc, props, configs) {
    try {
      if (window.TalkdeskChatSDK) {
        console.warn('TalkdeskChatSDK already included');
        return;
      }

      const container = document.createElement('div');
      container.id = nodeId;
      document.body.appendChild(container);

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.async = true;
      script.id = 'tdwebchatscript';
      script.src = scriptSrc;

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(script, firstScriptTag);

      script.onload = function() {
        try {
          const webchat = TalkdeskChatSDK(nodeId, props);
          webchat.init(configs);
          /*
           * Send custom data from your website to TalkDesk!
           * If you would like to do it, you need to remove the following commented code and
           * modify the webchat.setContextParam parameters to pass in the data you need.
           */
          /*
            function setContext() {
              webchat.setContextParam({ "var1": "value1", "var2": "value2", "var3": 100 })
            }
            // Send data when the chat conversation is initiated
            webchat.onConversationStart = function() {
              setContext()
            }
            // Send data when the chat widget is open
            webchat.onOpenWebchat = function() {
              setContext()
            }
          */
        } catch (innerError) {
          console.error('Error initializing Talkdesk Webchat SDK:', innerError);
          logErrorToSentry?.(innerError);
        }
      };

      script.onerror = function(e) {
        console.warn('Failed to load Talkdesk script:', scriptSrc, e);
      };
    } catch (error) {
      console.error('Unexpected error in talkdeskChatInit:', error);
      logErrorToSentry?.(error);
    }
  }

  withLineBreaks(string) {
    return !string ? '' : string.split(/\r\n|\n/g).join('<br>');
  }

  sendAnalyticsDealerPageEvent() {
    pushDataLayerEvent('elements_page_view');
  }

  get backgroundColor() {
    return {
      'background-color': isDealerPage() ? '#305371' : '#1F1F1F',
    };
  }

  get mainData() {
    return this.footerData.main;
  }

  get driversData() {
    return this.footerData.drivers;
  }

  get termsData() {
    return this.footerData.terms;
  }

  get primaryCtaData() {
    return JSON.stringify(this.mainData.primaryCta);
  }

  get footerData() {
    return {
      main: {
        header: 'Stay Stoked and in the Loop\nwith our Latest Innovations',
        primaryCta: {
          text: 'Sign up for our newsletter',
          url: 'https://mailchi.mp/3-form.com/newsletter',
          useWhite: true,
        },
        socials: [
          {
            text: 'Facebook',
            svgSrc: '/static/icons/SVG/facebook.svg',
            href: '//www.facebook.com/3formMaterialSolutions',
            class: 'icon-facebook',
            navString: 'EXIT|Global Footer|Facebook',
          },
          {
            text: 'Youtube',
            svgSrc: '/static/icons/SVG/youtube.svg',
            href: '//www.youtube.com/user/3formVideo',
            class: 'icon-youtube',
            navString: 'EXIT|Global Footer|Youtube',
          },
          {
            text: 'Pinterest',
            svgSrc: '/static/icons/SVG/pinterest.svg',
            href: '//www.pinterest.com/3formusa/',
            class: 'icon-pinterest',
            navString: 'EXIT|Global Footer|Pinterest',
          },
          {
            text: 'Instagram',
            svgSrc: '/static/icons/SVG/instagram.svg',
            href: '//instagram.com/3form/',
            class: 'icon-instagram',
            navString: 'EXIT|Global Footer|Instagram',
          },
        ],
      },
      drivers: [
        {
          category: 'Resources',
          links: [
            {
              text: 'Tech Specs & Downloads',
              href: '/tech-specs',
              navString: 'NAV|Global Footer|Tech Specs',
            },
            {
              text: 'BABAA Compliance',
              href: '/about/babaa',
              navString: 'NAV|Global Footer|BABAA Compliance',
            },
            {
              text: 'CEUs',
              href: isDealerPage() ? '/elements/ceus' : '/ceus',
              navString: 'NAV|Global Footer|CEUs',
            },
            {
              text: 'Hardware Catalog',
              href: 'https://cart.3-form.com/t/hardware-catalog',
              navString: 'NAV|Global Footer|Hardware Catalog',
            },
            {
              text: 'My3form',
              href: 'https://ngapps.3-form.com/account/login',
              navString: 'NAV|Global Footer|My3form',
            },
            {
              text: 'Find an Order',
              href: '/find-an-order',
              navString: 'NAV|Global Footer|Find an Order',
            },
          ],
        },
        {
          category: 'Orders',
          links: [
            {
              text: '3form Direct',
              href: '/direct',
              navString: 'NAV|Global Footer|Direct',
            },
            {
              text: 'Reclaim',
              href: 'https://3form-reclaim.myshopify.com/',
              target: '_blank',
              navString: 'EXIT|Global Footer|Reclaim',
            },
            {
              text: 'Ordering FAQs',
              href: '/order-faq',
              navString: 'NAV|Global Footer|Order FAQ',
            },
            {
              text: 'Conditions of Sale',
              href: '/conditions-of-sale',
              navString: 'NAV|Global Footer|Conditions of Sale',
            },
          ],
        },
        {
          category: 'Contact',
          links: [
            {
              svgSrc: '/static/icons/SVG/chat.svg',
              text: 'Chat',
              href: '',
              opensChat: true,
              navString: 'INP|Global Footer|Open Chat',
            },
            {
              division: '3form',
              svgSrc: '/static/icons/SVG/telephone.svg',
              text: '1 800 726 0126',
              href: 'tel:18007260126',
              navString: 'INP|Global Footer|3form Phone',
            },
            {
              division: '3form',
              svgSrc: '/static/icons/SVG/email.svg',
              text: 'info@3-form.com',
              href: 'mailto:info@3-form.com',
              navString: 'INP|Global Footer|Email',
            },
            {
              division: 'elements',
              svgSrc: '/static/icons/SVG/telephone.svg',
              text: '801-649-2522',
              href: 'tel:18016492522',
              navString: 'INP|Global Footer|Elements Phone',
            },
            {
              division: 'elements',
              svgSrc: '/static/icons/SVG/email.svg',
              text: 'elements@3-form.com',
              href: 'mailto:elements@3-form.com',
              navString: 'INP|Global Footer|Elements Email',
            },
            {
              svgSrc: '/static/icons/SVG/pin.svg',
              text: 'Locations',
              href: '/locations',
              navString: 'NAV|Global Footer|Locations',
            },
          ],
        },
      ],
      terms: {
        right: [
          {
            text: 'Careers',
            link: '/careers',
            navString: 'NAV|Global Footer|Careers',
          },
          {
            text: 'IP Notices',
            link: '/ip',
            navString: 'NAV|Global Footer|IP',
          },
          {
            text: 'Privacy',
            link: '/privacy',
            navString: 'NAV|Global Footer|Privacy',
          },
          {
            text: 'Your Privacy Choices',
            link: '/privacy-choices',
            navString: 'NAV|Global Footer|Privacy Choices',
          },
          {
            text: 'Forced Labor Policy',
            link: '/tech-specs/1705',
            target: '_blank',
            navString: 'NAV|Global Footer|Forced Labor Policy',
          },
          {
            text: 'Terms & Conditions',
            link: '/terms',
            navString: 'NAV|Global Footer|Terms',
          },
        ],
      },
    };
  }
}

customElements.define('global-footer', GlobalFooter);
